import React from "react"
import { Redirect, Route } from "react-router-dom"
import { AppContainer } from "#/components/containers/app/AppContainer"
import LoginContainer from "#/components/containers/login/LoginContainer"
import "#/css/transitions.css"
import { FadeTransition } from "./molecules/Molecules"
import ResetPasswordContainer from "../containers/login/ResetPasswordContainer"
import RequestResetPasswordContainer from "../containers/login/RequestResetPasswordContainer"
import ResetPasswordSuccess from "../../pages/reset-password-success"
import ResetPasswordError from "../../pages/reset-password-error"
import MerchantDeclineContainer from "../containers/merchantdecline/MerchantDeclineContainer"
import SignUp from "#/pages/signup"
import SignUpSuccess from "#/pages/signup-success"
import SignUpFindAccount from "#/pages/signup-find-account"
import SignUpFindAccountSuccess from "#/pages/signup-find-account-success"
import FindAccountByExternalEmployeeIdSuccess from "#/pages/signup-find-account-by-external-employee-id-success"
import SignUpGetHelp from "../../pages/signup-get-help"
import SignUpGetHelpSuccess from "../../pages/signup-get-help-success"
import SignUpEmployeeAlreadyRegistered from "../../pages/signup-employee-already-registered"
import SignUpEmployeeHasSaml from "../../pages/signup-employee-has-saml"
import SignUpInfo from "#/pages/signup/info"
import SignUpShareZipCode from "#/pages/signup-share-zip-code"
import { withLDProvider } from "launchdarkly-react-client-sdk"
import { basicLogger } from "launchdarkly-js-client-sdk"
import { carrotClient } from "#/utils/CarrotClient"
import Settings from "../../utils/CarrotConfig"
import { BiometricAuthEnrollmentContextProvider } from "../context/BiometricAuthEnrollment/BiometricAuthEnrollmentContext"
import PartnerRegistrationFlow from "../partner-access/partner-registration/PartnerRegistrationFlow"
import { LoadingInner } from "./Loading"
import SamlNoEmailOnboardingCaptureEmail from "../../pages/saml-no-email-onboarding-capture-email"
import { Switch } from "#/services/routing/Switch"
import { useConsumerRoutesEnabled } from "#/services/launchdarkly/useConsumerRoutesEnabled"
import { useLocaleContext } from "#/components/context/user/LocaleContext"
import { CarrotRxExtend } from "#/pages/carrot-rx-extend"
import { createErrorBoundary } from "#/utils/createErrorBoundary"
import { AppAccessError } from "#/components/views/AppAccessError"
import MultiFactorAuthContainer from "#/components/containers/login/MultiFactorAuthContainer"
import { RegistrationFlow } from "#/components/views/register/RegistrationFlow"
import { ConsumerSubscriptionRouteWithProfile } from "#/components/views/ConsumerSubscriptionRouteWithProfile"
import LegacyFunnelPathRedirect from "#/components/views/LegacyFunnelPathRedirect"
// TEMPORARY (CONAPP-155): the static distribution landing page mounted at `/consumer` below.
// Imported eagerly rather than lazily like the package surfaces — it's a thin iframe wrapper, and the
// page itself is a `public/` document the browser fetches, so there's nothing worth code-splitting.
import ConsumerLandingPage from "#/pages/consumer-landing/ConsumerLandingPage"

// The Consumer package surfaces. Both are mounted below, ABOVE the authenticated / unauthenticated
// split, so they render for signed-out members too (for now `/consumer` is public as well, not just
// `/consumer/payment`). Code-split and lazy-loaded, so none of the package's code loads until
// someone navigates to a `/consumer` path — including the subscription funnel, whose lazy import
// moved into `ConsumerSubscriptionRouteWithProfile` along with the member-profile query that
// prefills its details step.
const ConsumerRouter = React.lazy(() => import("@internal/consumer").then((m) => ({ default: m.ConsumerRouter })))

const Root = ({
  loggedIn,
  loadingIsLoggedIn,
  email
}: {
  loggedIn: boolean
  loadingIsLoggedIn: boolean
  email?: string
}) => {
  const { didCompiledMessagesLoad } = useLocaleContext()
  // Single gate for ALL consumer-app work (CAP-135): the `consumer-app-sign-up`
  // LaunchDarkly flag both mounts the `/consumer` routes below AND enables the
  // subscription funnel's net-new account provisioning (vs. its stub). Off ⇒ the
  // consumer app is entirely dark.
  //
  // Read through `useConsumerRoutesEnabled` rather than `useFlags` directly: a blocked
  // LaunchDarkly leaves the flag `undefined`, which a bare truthiness check treats as
  // "off" and silently deletes every `/consumer` route. See the hook for the details.
  // App.tsx gates its own `/consumer` pages on the SAME hook — the two must agree, or a
  // signed-in member reaches AppContainer here and falls through to PageNotFound there.
  const consumerAppSignUp = useConsumerRoutesEnabled()
  // Net-new self-serve signup (CAP-135): after the account step provisions the
  // member, establish a Carrot session so the PROTECTED checkout is reachable. Built
  // in the host (it needs carrotClient) and passed into the consumer package via its
  // host contract. Mirrors the SPA's proven prelogin -> auth/login sequence (a
  // consumer self-serve account is always password-auth, so prelogin's SSO branch
  // doesn't apply); an MFA-required response or any failure throws so the account
  // step surfaces it (and stays put) instead of dead-ending at checkout.
  //
  // prelogin's OUTCOME is deliberately ignored here, and stays ignored now that CAP-170
  // has landed and prelogin can answer ACTIVATION_REQUIRED for a consumer. On THIS path
  // the answer is already known and cannot change what we do: the member was provisioned
  // seconds ago and has not paid, so activation-required is the expected reply and
  // checkout is the only correct destination (same argument as the pinned advance in
  // SubscriptionAccount). Branching on it could only send a just-created member somewhere
  // that isn't payment. The call is kept because carrot-app's login expects to follow one.
  //
  // Acting on the outcome belongs on the RETURN-VISIT surface, and that is where CAP-170
  // put it: LoginContainer routes ACTIVATION_REQUIRED to a finish-activation sign-in card
  // and hands the member back to `/consumer/payment/checkout`. Nothing to mirror here.
  //
  // ⚠️ NO `dispatch(signIn())` here, on purpose. For a Consumer-only session it cannot
  // succeed — `GET /api/v2/users/me/is-logged-in` answers `false` for these members
  // (no Employee UserRole until CAP-142's activation cascade), so `fetchReduxData`
  // always lands on FETCH_IS_LOGGED_IN_FAILURE. Its only observable effect was
  // damage: the REQUEST it dispatches first flips `loadingIsLoggedIn`, and the
  // `showPage` gate below unmounts the ENTIRE app while that round trip runs — so
  // signup tore the funnel off the screen mid-submit, remounted it back on the
  // account step, and only then advanced to checkout.
  //
  // The funnel does not need redux for this: CAP-172 sources its session from
  // `subscription.getHostSession` (BFF -> /authorize/jwt), which does recognise a
  // Consumer session.
  //
  // RESTORE THIS when carrot-app makes `is-logged-in` true for Consumer sessions
  // (asks 1+2 on CONAPP-167). Until then redux stays signed-out for consumers, which
  // is CAP-172's stated invariant; the cost is that after that fix a paid consumer
  // navigating to `/` in-SPA would still see login until a reload.
  const handleConsumerLogin = React.useCallback(async (loginEmail: string, password: string): Promise<void> => {
    await carrotClient.prelogin(loginEmail)
    const response = await carrotClient.login(loginEmail, password)
    if (response?.message) {
      throw new Error(`consumer login incomplete: ${response.message}`)
    }
  }, [])
  // Whether carrot-app itself considers this member signed in — the SAME check redux
  // gates the app root on (`fetchReduxData` → `getIsUserLoggedIn`), asked directly so
  // the funnel can wait for a true answer without dispatching anything.
  //
  // A dispatch is exactly what must NOT happen here: `fetchReduxData` fires
  // FETCH_IS_LOGGED_IN_REQUEST first, which flips `loadingIsLoggedIn` and makes the
  // `showPage` gate below unmount the entire app mid-funnel (that is the damage
  // documented on `handleConsumerLogin` above). A bare read has no such side effect,
  // and the full page load in `handleExitToApp` re-derives redux from scratch anyway.
  //
  // Why this endpoint answers differently after payment: the activation cascade grants
  // the Employee UserRole (CAP-142) and the next request silently reissues the cookie
  // with it (CAP-156), so `is-logged-in` flips false → true for the SAME session.
  // Verified against stage 2026-08-17.
  //
  // Failure is reported as "not signed in" rather than thrown: the caller polls this,
  // and a network blip must not read as a terminal verdict.
  const handleProbeAppAccess = React.useCallback(async (): Promise<boolean> => {
    try {
      const response = await carrotClient.getIsUserLoggedIn()
      return response?.isLoggedIn === true
    } catch {
      return false
    }
  }, [])
  // A FULL page load, not `history.push("/")`. Redux `isLoggedIn` is still false at
  // this point — the in-funnel login never refreshed it (see `handleConsumerLogin`) —
  // so an in-SPA navigation reaches the app root and gets bounced to the login form by
  // the auth split at the bottom of this Switch. Reloading re-runs `fetchReduxData`
  // against the now-entitled cookie, which is the whole fix.
  const handleExitToApp = React.useCallback((): void => {
    window.location.assign("/")
  }, [])
  const showPage = didCompiledMessagesLoad && !loadingIsLoggedIn

  return (
    <div>
      {showPage ? (
        <Switch allowedRouters={[NonAuthenticatedRoutesContainer]}>
          <Route path="/register/partner/:guid" component={PartnerRegistrationFlow} />
          <Route path="/register/:guid" component={RegistrationFlow} />
          <Route path="/saml-no-email-onboarding" component={SamlNoEmailOnboardingCaptureEmail} />
          <Route path={"/carrot-rx-extend"} component={CarrotRxExtend} />
          {/* Consumer routes — gated behind the `consumer-app-sign-up` flag, so the whole consumer app
              is dark until it's enabled. Declared above the auth split so they resolve for signed-out
              members before the app/login branch. `/consumer/payment` is a PREFIX route (no
              `exact`): the package's nested SubscriptionRouter owns the funnel steps beneath it, and it
              MUST stay above the non-exact `/consumer` route so it isn't swallowed. Each is a separate
              gated child (not wrapped in a Fragment) so the custom Switch's child-type check passes. */}
          {/* Authenticated billing-detail page — hoisted ABOVE the public `/consumer`
              routes so a signed-in member gets the billing page inside the authed chrome
              (nav/sidebar/footer via AppContainer) instead of the public consumer app. When
              `consumerAppSignUp` is OFF this branch isn't mounted, but the bottom
              `loggedIn ? AppContainer` already handles this path (the public `/consumer` prefix
              route is likewise not mounted, so there's no collision). When it is ON, this
              exact-match authed route MUST precede the non-exact `/consumer` prefix route below
              so the prefix route can't swallow it.

              Unlike the rest of `/consumer`, this page is NOT public: it renders a member's own
              billing details. So the route is mounted for signed-out visitors too and mirrors
              the auth split at the bottom of this Switch — signed out renders the login page at
              this URL instead of falling through to the public consumer app (whose router would
              bounce the unknown subpath to `/consumer`). Because the URL is preserved, the
              billing page renders as soon as `loggedIn` flips after a successful sign-in.
              Kept as a discrete `{cond && <Route/>}` child (not wrapped in a Fragment) to
              satisfy the custom Switch's child-type rules.

              The path is an ARRAY so the pre-rename `/consumer/subscription-billing` is hoisted
              here too — otherwise the non-exact public `/consumer` route below swallows it and
              its unknown-subpath redirect bounces to `/consumer`, discarding the Stripe
              `setup_intent_client_secret` on a 3DS return issued before this rename shipped. The
              legacy path only gets as far as AppContainer here; App.tsx redirects it to the new
              one, search intact. */}
          {consumerAppSignUp && (
            <Route
              exact
              path={["/consumer/billing-detail", "/consumer/subscription-billing"]}
              render={() => (loggedIn ? <AppContainer key="app" /> : <NonAuthenticatedRoutesContainer />)}
            />
          )}
          {/* Authenticated FAQ page, hoisted for the same reason as the billing route directly above:
              a signed-in member gets it inside the authed chrome (nav/sidebar via AppContainer), and
              this exact-match route MUST precede the non-exact public `/consumer` prefix route below or
              that route swallows it and its unknown-subpath redirect bounces the member to `/consumer`.
              Its content is public copy, but the page lives in the authed nav alongside the member's
              other pages, so it mirrors the same auth split rather than rendering for signed-out
              visitors. Discrete `{cond && <Route/>}` child for the custom Switch's child-type check. */}
          {consumerAppSignUp && (
            <Route
              exact
              path="/consumer/faq"
              render={() => (loggedIn ? <AppContainer key="app" /> : <NonAuthenticatedRoutesContainer />)}
            />
          )}
          {consumerAppSignUp && (
            <Route
              path="/consumer/payment"
              render={() => (
                <React.Suspense fallback={<LoadingInner />}>
                  <ConsumerSubscriptionRouteWithProfile
                    isLoggedIn={loggedIn}
                    email={email}
                    stripePublishableKey={Settings.STRIPE_PUBLISHABLE_KEY}
                    selfServeSignupEnabled={consumerAppSignUp}
                    onLogin={handleConsumerLogin}
                    onProbeAppAccess={handleProbeAppAccess}
                    onExitToApp={handleExitToApp}
                  />
                </React.Suspense>
              )}
            />
          )}
          {/* The pre-rename funnel path (CONAPP-385), forwarded to the route directly above with the
              tail, query and hash intact — see `LegacyFunnelPathRedirect` for why the query matters.
              A PREFIX route like the one it mirrors, so it catches every step and not just the root.
              It does NOT swallow `/consumer/subscription-billing`: react-router matches on segment
              boundaries, which is also why that route only needed hoisting above the non-exact
              `/consumer` route below. MUST stay above that route itself, though, or the public
              consumer app takes these urls and bounces the unknown subpath to `/consumer`. Gated on
              the same flag as the rest of the consumer app, and a discrete `{cond && <Route/>}`
              child for the custom Switch's child-type check. */}
          {consumerAppSignUp && <Route path="/consumer/subscription" component={LegacyFunnelPathRedirect} />}
          {/* TEMPORARY (CONAPP-155) — static distribution landing page. Shadows the consumer app's own
              `/consumer` home (`ConsumerRouter` → `ConsumerHome`) until the temporary page is pulled;
              every other `/consumer/*` path still resolves through `ConsumerRouter` below, whose
              unknown-path redirect back to `/consumer` now lands here. `exact` so it takes only
              `/consumer` itself, and it MUST stay above the non-exact `/consumer` prefix route below or
              that route swallows it. Gated on the same `consumerAppSignUp` flag as the rest of the
              consumer app, and kept as a discrete `{cond && <Route/>}` child for the custom Switch's
              child-type check. Removal: delete this block, the import above,
              `src/pages/consumer-landing/`, and `public/consumer-landing.html`. */}
          {consumerAppSignUp && <Route exact path="/consumer" component={ConsumerLandingPage} />}
          {consumerAppSignUp && (
            <Route
              path="/consumer"
              render={() => (
                <React.Suspense fallback={<LoadingInner />}>
                  <ConsumerRouter isLoggedIn={loggedIn} />
                </React.Suspense>
              )}
            />
          )}
          {loggedIn ? <Route render={() => <AppContainer key="app" />} /> : <NonAuthenticatedRoutesContainer />}
        </Switch>
      ) : (
        <LoadingInner />
      )}
    </div>
  )
}

const NonAuthenticatedRoutesErrorBoundary = createErrorBoundary(AppAccessError, "NonAuthenticatedRoutesContainer")

const NonAuthenticatedRoutesContainer = () => {
  return (
    <NonAuthenticatedRoutesErrorBoundary>
      <Switch>
        <Route path="/signup/info/:parentCompanyId" component={SignUpInfo} />
        <Route path="/signup/info" component={SignUpInfo} />
        <Route
          path="/signup"
          render={() => (
            <FadeTransition>
              <SignUp />
            </FadeTransition>
          )}
        />

        <Route path="/signup-success" component={SignUpSuccess} />
        <Route path="/signup-find-account" component={SignUpFindAccount} />
        <Route path="/signup-find-account-success" component={SignUpFindAccountSuccess} />
        <Route
          path="/signup-find-account-by-external-employee-id-success"
          component={FindAccountByExternalEmployeeIdSuccess}
        />
        <Route path="/signup-get-help" component={SignUpGetHelp} />
        <Route path="/signup-get-help-success" component={SignUpGetHelpSuccess} />
        <Route path="/signup-employee-already-registered" component={SignUpEmployeeAlreadyRegistered} />
        <Route path="/signup-employee-has-saml" component={SignUpEmployeeHasSaml} />
        <Route path="/signup-share-zip-code" component={SignUpShareZipCode} />
        <Route path="/reset-password-success" component={ResetPasswordSuccess} />
        <Route path="/reset-password-error" component={ResetPasswordError} />
        <Route exact path="/reset-password" component={ResetPasswordContainer} />
        <Route path="/request-reset-password" component={RequestResetPasswordContainer} />
        <Route path="/confirm/:token" component={MerchantDeclineContainer} />
        <Route path="/login/enter-code" component={MultiFactorAuthContainer} />

        <Redirect exact path="/sign-up" to="/signup" />
        {/* This needs to stay on the bottom since it doesn't have a path and will override any routes declared below it. */}
        <Route
          render={() => (
            <FadeTransition>
              <BiometricAuthEnrollmentContextProvider>
                <LoginContainer key="login" />
              </BiometricAuthEnrollmentContextProvider>
            </FadeTransition>
          )}
        />
      </Switch>
    </NonAuthenticatedRoutesErrorBoundary>
  )
}

export default withLDProvider({
  clientSideID: Settings.LAUNCH_DARKLY_CLIENT_ID,
  user: {
    key: "anon",
    email: "anon@get-carrot.com"
  },
  timeout: 3,
  options: {
    logger: basicLogger({ level: "warn" })
  }
})(Root)
